import { Controller } from '@hotwired/stimulus'
import { broadcastInputChange } from '../_broadcast_change_utils'

export default class extends Controller<HTMLLabelElement> {
  static targets = ['control', 'indicator', 'input']

  static values = {
    overrides: Object
  }

  declare controlTarget: HTMLElement
  declare inputTarget: HTMLInputElement
  declare indicatorTarget: HTMLElement
  declare hasInputTarget: boolean

  declare readonly overridesValue: { on: string | number; off: string | number } | { x?: never }

  connect(): void {
    console.log('[ui-boolean] connect', {
      element: this.element,
      hasInputTarget: this.hasInputTarget,
      valueBeforeNormalize: this.hasInputTarget ? this.inputTarget.value : null
    })
    this.#normaliseInputValue()
    this.#syncLinkedPanels()
    console.log('[ui-boolean] connect:after-normalize', {
      value: this.hasInputTarget ? this.inputTarget.value : null
    })
  }

  #valueToUse(booleanValue: boolean): string {
    if ('on' in this.overridesValue) {
      return booleanValue ? String(this.overridesValue.on) : String(this.overridesValue.off)
    }
    return booleanValue ? '1' : '0'
  }

  #normaliseInputValue(): void {
    if (!this.hasInputTarget) return

    if (this.inputTarget.value !== this.#valueToUse(true)) {
      this.inputTarget.value = this.#valueToUse(false)
    }
  }

  toggle(): void {
    if (!this.hasInputTarget) return

    console.log('[ui-boolean] toggle:start', {
      input: this.inputTarget,
      value: this.inputTarget.value
    })
    this.controlTarget.classList.toggle('bg-300')
    this.controlTarget.classList.toggle('bg-green')
    this.indicatorTarget.classList.toggle('translate-x-full')

    const newValue =
      this.inputTarget.value === this.#valueToUse(false)
        ? this.#valueToUse(true)
        : this.#valueToUse(false)
    this.inputTarget.value = newValue

    console.log('[ui-boolean] toggle:after-set', {
      input: this.inputTarget,
      value: this.inputTarget.value
    })

    broadcastInputChange(this.inputTarget)
    console.log('[ui-boolean] toggle:after-broadcast-change', {
      value: this.inputTarget.value
    })
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.#syncLinkedPanels()
    console.log('[ui-boolean] toggle:after-dispatch-change', {
      value: this.inputTarget.value
    })
  }

  keydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      this.toggle()
      e.preventDefault()
    }
  }

  #syncLinkedPanels(): void {
    if (!this.hasInputTarget) return

    const selector = this.inputTarget.dataset.togglePanelsSelector
    if (selector == null || selector === '') return

    const enabled = this.inputTarget.value === this.#valueToUse(true)
    const panels = document.querySelectorAll<HTMLElement>(selector)

    console.log('[ui-boolean] syncLinkedPanels', {
      selector,
      enabled,
      panelCount: panels.length
    })

    panels.forEach(panel => {
      panel.classList.toggle('hidden', !enabled)
      panel
        .querySelectorAll<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >('input, textarea, select')
        .forEach(field => {
          field.disabled = !enabled
        })
    })
  }
}
